import { Draw } from 'components/PdfViewer';
import { generateWholeRandomNumber } from 'utils/generateRandomNumber';
import { useCallback, useEffect, useRef, useState } from 'react';
import { signersColorsArray } from 'utils/signersColors';
import { v4 } from 'uuid';
import { generateImageSignerPlaceholder } from 'utils/signatures';

const useGraphSign = (): any => {
    const evidences = useRef({});
    const signatureRef = useRef<any>();
    const [draws, setDraws] = useState<Draw[]>([]);
    const onMouseDownRef = useRef<boolean>(false);
    const [pages, setPages] = useState<number[]>([1]);
    const [showSignature, setShowSignature] = useState<boolean>(false);
    const drawsRef = useRef<Draw[]>(draws);
    const showSignatureRef = useRef(showSignature);

    useEffect(() => {
        drawsRef.current = draws;
    }, [draws]);

    useEffect(() => {
        showSignatureRef.current = showSignature;
    }, [showSignature]);

    const lastRenderAttempt = useRef<Record<string, any>>({});
    const errorCounter = useRef<number>(0);

    useEffect(() => {
        const listener = (e: any) => {
            if (e.reason?.message === "Cannot read properties of undefined (reading 'registerAnnotation')") {
                const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
                const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
                if (resizeObserverErr) {
                    resizeObserverErr.setAttribute('style', 'display: none');
                }
                if (resizeObserverErrDiv) {
                    resizeObserverErrDiv.setAttribute('style', 'display: none');
                }
            }
            e.preventDefault();
            if (errorCounter.current < 30) {
                errorCounter.current++;
                setTimeout(() => {
                    lastRenderAttempt.current?.fileId &&
                        drawsRef.current.length &&
                        renderDraws(lastRenderAttempt.current?.fileId, lastRenderAttempt.current.content);
                }, 200);
            }
        };
        window.addEventListener('unhandledrejection', listener);
        return () => {
            window.removeEventListener('unhandledrejection', listener);
        };
    }, []);
    const fillEvidences = (data: any) => {
        evidences.current = { ...evidences.current, ...data };
    };

    const resetDraw = () => {
        (window as any).PDFViewerApplication?.pdfViewer?.removeRegisteredAnotations();
        setDraws([]);
    };

    const removeDraw = (id: string) => {
        (window as any).PDFViewerApplication?.pdfViewer?.removeGroupedAnnotations(id);
        setDraws(draws.filter((_draw: any) => _draw.id !== id));
    };

    const removeAllDrawsWhithSameSigner = (id: string) => {
        setDraws(draws.filter((_draw: any) => _draw.signerId !== id));
        const drawsToRemove = draws.filter((_draw: any) => _draw.signerId === id);
        drawsToRemove.forEach((_draw: any) => {
            (window as any).PDFViewerApplication?.pdfViewer?.removeGroupedAnnotations(_draw.id);
        });
    };

    const drawsWithSameSigner = (id: string, fileId: string) => {
        return draws.filter((_draw) => _draw.signerId === id && fileId === _draw.fileId);
    };

    // Estable entre renders (solo lee refs): puede usarse como dependencia de
    // efectos en useSendSignDocument/useSignDocument sin reprogramarlos.
    const renderDraws = useCallback((fileId: string, content?: string, tries = 0) => {
        let count: Record<string, number> = {};
        lastRenderAttempt.current = {
            fileId,
            content
        };
        setTimeout(() => {
            (window as any).PDFViewerApplication?.pdfViewer?.removeRegisteredAnotations();
            if (tries > 5) {
                return;
            }

            const shouldRenderStamps = showSignatureRef.current || Boolean(content);

            shouldRenderStamps &&
                drawsRef.current.forEach((draw) => {
                    if (draw.fileId === fileId) {
                        const placeHolder2 =
                            draw.signerId && count[draw.signerId]
                                ? (count[draw.signerId] = count[draw.signerId] + 1)
                                : draw.signerId
                                ? (count[draw.signerId] = 1)
                                : null;

                        if ((window as any).PDFViewerApplication?.pdfViewer) {
                            (window as any).PDFViewerApplication?.pdfViewer.registerStampAnnotations({
                                group: draw.id,
                                groupColor: draw.groupColor,
                                pages: draw.pages?.length ? draw.pages : [1],
                                top: draw.top,
                                left: draw.left,
                                width: draw.width,
                                height: draw.height,
                                imageUrl:
                                    content ||
                                    (draw.placeHolder &&
                                        generateImageSignerPlaceholder(
                                            draw.placeHolder[0],
                                            draw.placeHolder[1].replace('{{NUMEROFIRMA}}', String(placeHolder2))
                                        ))
                            });
                        } else {
                            setTimeout(() => {
                                renderDraws(fileId, content, tries + 1);
                            }, 250);
                        }
                    }
                });
        }, 500);
    }, []);

    const setMultiSignature = (params: Record<string, any>[]) => {
        if (params.length === 0) return;

        setDraws((currentDraws) => {
            let colorIndex = currentDraws.length;
            const newDraws = params.map((param) => {
                const templateDraw = param.templateDraw;
                const newDraw: Draw = {
                    ...param.sig,
                    id: v4(),
                    top: templateDraw?.top ?? generateWholeRandomNumber(100, 700),
                    left: templateDraw?.left ?? generateWholeRandomNumber(50, 500),
                    width: templateDraw?.width ?? param.sig?.width ?? 200,
                    height: templateDraw?.height ?? param.sig?.height ?? 96,
                    signerId: param.id,
                    imageUrl: param.sig?.content,
                    groupColor: signersColorsArray[colorIndex % signersColorsArray.length],
                    fileId: param.fileId,
                    pages: templateDraw?.pages?.length ? templateDraw.pages : [1],
                    placeHolder: param.placeHolder,
                    lockSignature: templateDraw?.lockSignature
                };
                colorIndex++;
                return newDraw;
            });

            return [...currentDraws, ...newDraws];
        });
    };
    const setSignature = (sig: any, signerId: string, fileId: string, placeHolder?: string[]) => {
        setDraws((currentDraws) => {
            const newDraw: Draw = {
                ...sig,
                id: v4(),
                top: generateWholeRandomNumber(100, 700),
                left: generateWholeRandomNumber(50, 500),
                signerId,
                imageUrl: sig.content,
                groupColor: signersColorsArray[currentDraws.length % signersColorsArray.length],
                fileId: fileId,
                placeHolder: placeHolder
            };
            return [...currentDraws, newDraw];
        });
    };

    return {
        fillEvidences,
        evidences,
        setDraw: setPages,
        draws,
        setDraws,
        resetDraw,
        signature: signatureRef.current,
        setSignature,
        setMultiSignature,
        signatureRef,
        onMouseDownRef,
        pages,
        removeDraw,
        renderDraws,
        drawsWithSameSigner,
        removeAllDrawsWhithSameSigner,
        showSignature,
        setShowSignature
    };
};

export default useGraphSign;
